import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import { BUTTON_TYPES, CARD_TYPES } from '../../../shared/constants';
import { IOverviewCard } from '../../../types/shared';

import { ChevronRight } from '../../atoms/icons/ChevronRight';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink, getProductLinkFromPage } from '../../../utils/generic';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';
import { RichTextHtml } from '../../../utils/richtext/richTextRender';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { ButtonLabel, H3 } from '../../../styles/generic';
import { Theme } from '../../../styles/themes';

const iconStyle = { width: '24px', height: '24px', color: 'transparent' };

const ProductOverviewCard = ({
  title,
  subtitle,
  image,
  body,
  linkedPage,
  conditions,
  comingSoon,
  primaryLink,
  service,
  className,
  layout,
  externalLink,
}: IOverviewCard) => {
  let productLink: string | undefined;
  if (primaryLink) {
    productLink = getCtaLink(primaryLink);
  }
  if (externalLink || linkedPage) {
    productLink = externalLink || getProductLinkFromPage(linkedPage)[0];
  }
  const buttonType = externalLink
    ? BUTTON_TYPES.external
    : BUTTON_TYPES.internal;

  const innerContent = () => (
    <ContentWrapper layout={layout}>
      {comingSoon && <Tag>Binnenkort beschikbaar</Tag>}
      {image && image.file && (
        <ImageWrapper layout={layout} yellow={service}>
          <Image src={image.file?.url} alt={subtitle || ''} />
        </ImageWrapper>
      )}
      <TextContainer data-component-type="ProductOverviewCard">
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        {title && <StyledH3>{title}</StyledH3>}
        {body?.raw && (
          <Description>
            <RichTextHtml
              richTextNode={body}
              deqCompType={CARD_TYPES.productCard}
            />
          </Description>
        )}
        {body?.childMarkdownRemark && (
          <ReactMarkdownExtended componentType={CARD_TYPES.productCard}>
            {body.childMarkdownRemark.rawMarkdownBody}
          </ReactMarkdownExtended>
        )}
        {conditions && (
          <Conditions>
            {conditions.map((condition) => (
              <Condition key={condition.title}>
                <GatsbyImage
                  imgStyle={iconStyle}
                  style={iconStyle}
                  alt=""
                  image={condition.image.gatsbyImageData}
                />
                {condition.title}
              </Condition>
            ))}
          </Conditions>
        )}
        {primaryLink && (
          <LinkText>
            {primaryLink.label}
            <ChevronRight />
          </LinkText>
        )}
      </TextContainer>
    </ContentWrapper>
  );

  return comingSoon ? (
    <Container faded className={className}>
      {innerContent()}
    </Container>
  ) : (
    <LinkContainer
      className={className}
      href={productLink}
      target={buttonType === BUTTON_TYPES.external ? '_blank' : ''}
      data-component-type="ProductOverviewCard"
      data-element-type={DEQ_TYPES.button}
      data-element-label={title?.toLowerCase()}
      data-custom-object={JSON.stringify({
        elementTarget: `${productLink}`,
      })}
      rel="noreferrer"
    >
      {innerContent()}
    </LinkContainer>
  );
};

export default ProductOverviewCard;

const containerStyles = css<{ faded?: boolean }>`
  background-color: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  box-sizing: border-box;
  cursor: pointer;
  display: block;
  flex-direction: column;
  margin-bottom: 32px;
  padding: 40px 24px 32px;
  position: relative;
  ${({ faded }) =>
    faded &&
    css`
      cursor: default;
      &:after {
        background-color: ${Colors.white};
        content: '';
        height: 100%;
        left: 0;
        opacity: 0.5;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: 1;
      }
    `};

  ${mq.from.breakpoint.L`
  padding: 40px 48px;
`}

  ${mq.from.breakpoint.XL`
  flex-direction: row;
`}
`;

const LinkContainer = styled.a`
  ${containerStyles};
  text-decoration: none;
  transition: box-shadow 0.3s ease-in-out;

  &:hover {
    box-shadow: ${({ theme }) => theme.elevations.abnElevationDropdown};
  }
`;

const ContentWrapper = styled.div<{ layout?: string }>`
  align-items: center;
  display: flex;
  ${({ layout }) => layout === 'portrait' && 'text-align: center'};
  flex-direction: ${({ layout }) => (layout === 'portrait' ? 'column' : 'row')};
`;

const Container = styled.div`
  ${containerStyles};
`;

const Tag = styled.div`
  background-color: ${Colors.abnYellow};
  font-family: ${({ theme }) => theme.fontCondensed};
  font-weight: 700;
  left: 0;
  padding: 16px 32px;
  position: absolute;
  top: 0;
  z-index: 2;
`;

const Subtitle = styled.span`
  color: ${Colors.abnGreen300};
  display: block;
  line-height: 16px;
  margin: 0 0 16px;
`;

const ImageWrapper = styled.div<{ layout?: string; yellow?: boolean }>`
  display: none;

  ${mq.from.breakpoint.M`
    align-items: center;
    display: flex;
    justify-content: center;
    height: 96px;
    margin-right: ${({ layout }: { layout: string }) =>
      layout === 'portrait' ? '0' : '48px;'};
    position: relative;
    width: 96px;
  
    &:after {
      background-color: ${({
        yellow,
        theme,
      }: {
        yellow: boolean;
        theme: Theme;
      }) => (yellow ? theme.colors.abnNeutral50 : Colors.abnZachtgeel)};
      border-radius: ${({ theme }) => theme.radiuses.XXL};
      content: '';
      height: inherit;
      width: inherit;
    }
  `}

  ${mq.from.breakpoint.L`
    height: 144px;
    width: 144px;
  `}
`;

const Image = styled.img`
  height: 64px;
  position: absolute;
  width: 64px;

  ${mq.from.breakpoint.L`
    width: 96px;
    height: 96px;
  `}
`;

const TextContainer = styled.div`
  margin-top: 0;

  ${mq.from.breakpoint.M`
    margin-top: 24px;
  `}

  ${mq.from.breakpoint.XL`
    margin-top: 48px;
    align-self: center;
  `}
`;
const Description = styled.div`
  line-height: 24px;

  > p {
    margin-bottom: 0;
  }
`;

const LinkText = styled.div`
  align-items: center;
  color: ${Colors.abnGreen300};
  display: flex;
  flex-direction: row;
  font-family: ${({ theme }) => theme.fontRegular};
  gap: 8px;
  justify-content: center;
  margin-top: 24px;
  ${ButtonLabel}
`;

const StyledH3 = styled(H3)`
  margin: 0;
`;

const Conditions = styled.div`
  margin-top: 24px;

  ${mq.from.breakpoint.XL`
    display: flex;
    align-items: center;
    margin-top: 32px;
  `}
`;

const Condition = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.abnGreen400};
  display: flex;
  margin-bottom: 16px;

  ${mq.from.breakpoint.XL`
    margin: 0 24px 0 0;
    width: 30%;

    &:last-of-type {
      margin: 0;
    }
  `}

  > div {
    flex-shrink: 0;
    margin-right: 8px;
  }
`;
